// @ts-strict-ignore
import {
  Box,
  Icon,
  useSnackBar,
} from '@usealloy/component-library';
import {
  useContext,
  useMemo,
  useState,
} from 'react';

import { formatColor } from 'flow/editor/components/helpers';
import {
  getActionName,
  getOutcomeBaseColor,
  isPreviewOutcomeAction,
} from 'flow/service/actions';
import NodeDropdownMenu from 'flow/editor/components/Graph/nodes/BaseNode/NodeDropdownMenu';
import {
  selectMergeDiff,
  selectNodeState,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import {
  StyledBaseNode,
  StyledNodeText,
} from 'flow/editor/components/Graph/nodes/BaseNode/BaseNode.styles';

import { UserContext } from '~/dashboard/context';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  getDropdownItems,
  getOutcomeIconName,
  getOutcomeStateStyle,
} from './helpers';
import { OnIslandOutcomeNodeContainer } from './styles';
import type { BaseOnIslandNodeProps } from '../../types';
import ConfirmNodeDeletionModal from '../../../Graph/nodes/BaseNode/ConfirmNodeDeletionModal';
import { useEditorAccess } from 'flow/editor/context';
import { checkIfEditorModeIsEditable } from 'flow/service/general';
import { selectEvaluationOutcome } from 'flow/store/redux/selectors/workflowFeatures';
import { getCanHoverOnNode } from 'flow/editor/components/Graph/nodes/BaseNode/helpers';


export default function OnIslandOutcomeNode({
  action,
  allActionsOfActionType,
  isAddingNewAction = false,
  islandMode = 'expanded',
  onClick,
  onMouseEnter,
  onMouseLeave,
  onSwapOutcome,
}: BaseOnIslandNodeProps) {
  const { featureFlags } = useContext(UserContext);
  const {
    outcome: {
      color,
      icon,
      name,
    },
    publicId,
  } = action;
  const dispatch = useAppDispatch();
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);
  const nodeState = useAppSelector(state => selectNodeState(state, publicId));
  const mergeDiff = useAppSelector(selectMergeDiff);

  const outcomeActionName = getActionName(action);
  const baseBackgroundColor = color || getOutcomeBaseColor(outcomeActionName);
  const backgroundColor = formatColor(baseBackgroundColor);
  const outcomeIconName = icon || getOutcomeIconName(outcomeActionName);
  const nodeStateStyle = getOutcomeStateStyle({
    outcomeAction: action,
    nodeState,
  });

  const [nodeDeletionChildActionCounts, setNodeDeletionChildActionCounts] = useState<number>(0);
  const resetNodeChildActionCounts = () => setNodeDeletionChildActionCounts(0);

  const { editorMode } = useEditorAccess();
  const inEditMode = checkIfEditorModeIsEditable(editorMode);

  const addSnackBar = useSnackBar();

  const dropdownItems = useMemo(() => getDropdownItems({
    addSnackBar,
    dispatch,
    outcomeAction: action,
    outcomeActions: allActionsOfActionType,
    workflowData,
    workflowProps,
    setNodeDeletionChildActionCounts,
    onSwapOutcome,
  }), [
    addSnackBar,
    dispatch,
    action,
    allActionsOfActionType,
    onSwapOutcome,
    workflowData,
    workflowProps,
  ]);

  const evaluationOutcome = useAppSelector(selectEvaluationOutcome);
  const isOutcomeSet = evaluationOutcome === action.outcome?.name;
  const ruleExplainabilityMode = !inEditMode && featureFlags?.ruleExplainabilityPhase >= 4
    && evaluationOutcome;

  const canHover = getCanHoverOnNode(mergeDiff, publicId);

  return (
    <OnIslandOutcomeNodeContainer
      onClick={event => onClick?.(event, action)}
      onMouseEnter={canHover ? event => onMouseEnter(event, action) : undefined}
      onMouseLeave={canHover ? event => onMouseLeave(event, action) : undefined}
      outcomeIslandMode={islandMode}
      dimOutcome={ruleExplainabilityMode && !isOutcomeSet
        && (nodeState !== 'focused' && nodeState !== 'focused-hovered')}
      data-dd-action-name={`outcome-island-outcome-${name}`}
    >
      {/* todo - think how to best reuse the node styles. can happen later */}
      <StyledBaseNode
        baseBackgroundColor={backgroundColor}
        nodeState={nodeState}
        style={nodeStateStyle}
      >
        <Box gap={1} alignItems="center" justifyContent="flex-start">
          <Icon
            icon={outcomeIconName}
            style={{ color: nodeStateStyle.color }}
          />
          <StyledNodeText nodeState={nodeState}>{name}</StyledNodeText>
        </Box>
        {
          (nodeState === 'focused' || nodeState === 'focused-hovered')
          && !isPreviewOutcomeAction(action)
          && !isAddingNewAction
          && inEditMode
          && (
            <NodeDropdownMenu
              label={name}
              backgroundColor={backgroundColor}
              dropdownItems={dropdownItems}
            />
          )
        }

        {nodeDeletionChildActionCounts > 0 && (
          <ConfirmNodeDeletionModal
            nodeDeletionChildActionCounts={nodeDeletionChildActionCounts}
            resetNodeChildActionCounts={resetNodeChildActionCounts}
            id={publicId}
          />
        )}
      </StyledBaseNode>
    </OnIslandOutcomeNodeContainer>
  );
}
